import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
} from 'recharts';
import ChartDefaults from '../../shared/ChartDefaults';
import Utils from '../../shared/Utils';

const labelFormatter = (value) => ((value === 0) ? '' : `${value}%`);

const formatData = (baselineData, userData, decimalPlaces) => {
  const array = baselineData || userData;
  if (!array || array.length === 0) {
    return [];
  }

  return array.map((result, i) => {
    const dataPoint = {
      name: result.label,
    };

    if (baselineData) {
      dataPoint.baseline = Utils.roundFormat(baselineData[i].value * 100, decimalPlaces, ChartDefaults.decimalPlaces);
    }

    if (userData) {
      dataPoint.user = Utils.roundFormat(userData[i].value * 100, decimalPlaces, ChartDefaults.decimalPlaces);
    }

    return dataPoint;
  });
};

const calculateYAxisWidth = (data) => {
  let maxWidth = 0;
  data.forEach((item) => {
    const width = Utils.estimateTextWidth(
      item.name,
      ChartDefaults.BarChartStyleOptions.fontSize,
      ChartDefaults.BarChartStyleOptions.fontFamily,
      ChartDefaults.BarChartStyleOptions.fontWeight,
    );
    maxWidth = Math.max(maxWidth, width);
  });
  return maxWidth;
};

const clipYAxisLabel = (value, maxWidth) => {
  if (!maxWidth) {
    return value;
  }
  const text = `${value ?? ''}`;
  if (!text) {
    return text;
  }
  const { fontSize, fontFamily, fontWeight } = ChartDefaults.BarChartStyleOptions;
  const fullWidth = Utils.estimateTextWidth(text, fontSize, fontFamily, fontWeight);
  if (fullWidth <= maxWidth) {
    return text;
  }

  if (maxWidth <= 0) {
    return '';
  }

  const ellipsis = '…';
  const ellipsisWidth = Utils.estimateTextWidth(ellipsis, fontSize, fontFamily, fontWeight);
  const availableWidth = Math.max(0, maxWidth - ellipsisWidth);
  if (availableWidth <= 0) {
    return ellipsis;
  }

  for (let i = text.length; i > 0; i -= 1) {
    const candidate = text.slice(0, i);
    const candidateWidth = Utils.estimateTextWidth(candidate, fontSize, fontFamily, fontWeight);
    if (candidateWidth <= availableWidth) {
      return `${candidate}${ellipsis}`;
    }
  }

  return ellipsis;
};

export default ({
  baselineData, userData, config, generalConfig, chartRef, className,
}) => {
  const chartData = formatData(baselineData, userData, (config.decimalPlaces !== undefined) ? config.decimalPlaces : 2);

  const heightCss = config.height || `${chartData.length * 80}px`;
  const [svgWidth, setSvgWidth] = React.useState(undefined);

  const margins = {};
  ['Left', 'Right', 'Top', 'Bottom'].forEach((p) => {
    const paddingKey = `padding${p}`;
    if (config[paddingKey]) {
      margins[p.toLowerCase()] = parseInt(config[paddingKey], 10);
    }
  });

  const [paddingBottomAuto, setPaddingBottomAuto] = React.useState(0);
  const [paddingLeftAuto, setPaddingLeftAuto] = React.useState(undefined);
  const [paddingRightAuto, setPaddingRightAuto] = React.useState(undefined);

  const adjustMargins = (svg) => {
    if (!svg) {
      return;
    }

    const yAxis = svg.getElementsByClassName('recharts-yAxis')[0];
    const xAxis = svg.getElementsByClassName('recharts-xAxis')[0];

    if (!xAxis || !yAxis) {
      return;
    }

    const canvasLeft = svg.getBoundingClientRect().x;
    const getLeftBound = (el) => {
      if (!el?.getBoundingClientRect) {
        return undefined;
      }
      let left = el.getBoundingClientRect().x;
      const bboxRect = Utils.getBoundsFromBBox(el);
      if (bboxRect && Number.isFinite(bboxRect.left)) {
        left = Math.min(left, bboxRect.left);
      }
      return left;
    };

    let minAxisLeft = Math.min(
      getLeftBound(yAxis) ?? canvasLeft,
      getLeftBound(xAxis) ?? canvasLeft,
    );
    const yAxisTicks = yAxis.getElementsByClassName('recharts-cartesian-axis-tick-value');
    [].forEach.call(yAxisTicks, (tick) => {
      const tickLeft = getLeftBound(tick);
      if (Number.isFinite(tickLeft)) {
        minAxisLeft = Math.min(minAxisLeft, tickLeft);
      }
    });
    const yAxisClipped = canvasLeft - minAxisLeft;
    setPaddingLeftAuto(yAxisClipped);

    const canvasBottom = svg.getBoundingClientRect().y + svg.getBoundingClientRect().height;
    const xAxisBottom = xAxis.getBoundingClientRect().y + xAxis.getBoundingClientRect().height;
    const xAxisClipped = xAxisBottom - canvasBottom;
    setPaddingBottomAuto(xAxisClipped > 0 ? xAxisClipped : 0);

    const canvasRight = svg.getBoundingClientRect().x + svg.getBoundingClientRect().width;
    const currentPaddingRight = margins.right || 0;
    let deltaRight = 0;
    const extendRightPadding = (el) => {
      if (!el?.getBoundingClientRect) {
        return;
      }
      const clientRect = el.getBoundingClientRect();
      const clientRight = clientRect.x + clientRect.width;
      const clientDelta = currentPaddingRight + clientRight - canvasRight;
      if (Number.isFinite(clientDelta)) {
        deltaRight = Math.max(deltaRight, clientDelta);
      }

      const bboxRect = Utils.getBoundsFromBBox(el);
      if (bboxRect) {
        const bboxRight = bboxRect.x + bboxRect.width;
        const bboxDelta = currentPaddingRight + bboxRight - canvasRight;
        if (Number.isFinite(bboxDelta)) {
          deltaRight = Math.max(deltaRight, bboxDelta);
        }
      }
    };

    [].forEach.call(svg.getElementsByClassName('recharts-bar'), extendRightPadding);

    if (!Number.isFinite(deltaRight) || deltaRight < 0) {
      setPaddingRightAuto(undefined);
    } else {
      setPaddingRightAuto(deltaRight);
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (chartData && chartData.length > 0) {
        const svg = Utils.getSvg(chartRef);
        if (svg) {
          svg.style.background = 'white';
          adjustMargins(svg);
        }
      }
    }, 10);
    return () => clearTimeout(timeoutId);
  }, [baselineData, userData, config]);

  React.useEffect(() => {
    const container = chartRef?.current;
    if (!container) return undefined;

    const update = () => {
      const svg = Utils.getSvg(chartRef);
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      if (Number.isFinite(rect.width) && rect.width > 0) {
        setSvgWidth(rect.width);
      }
    };

    update();
    const raf1 = requestAnimationFrame(update);
    const raf2 = requestAnimationFrame(update);

    const ro = new ResizeObserver(() => update());
    ro.observe(container);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [chartRef]);

  const yAxisWidth = React.useMemo(() => {
    if (!svgWidth || !chartData?.length) {
      return undefined;
    }
    const requiredWidth = calculateYAxisWidth(chartData);
    return Math.min(requiredWidth, svgWidth * 0.5);
  }, [chartData, svgWidth]);

  margins.bottom = margins.bottom !== undefined ? margins.bottom : paddingBottomAuto;
  margins.left = margins.left !== undefined ? margins.left : paddingLeftAuto;
  margins.right = margins.right !== undefined ? margins.right : paddingRightAuto;

  let maxValue = 0;
  if (chartData && chartData.length > 0) {
    chartData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'name' && typeof item[key] === 'number') {
          maxValue = Math.max(maxValue, item[key]);
        }
      });
    });
  }
  const xDomainMax = Math.min(100, Math.max(10, maxValue));

  const axisTickStyle = { fontSize: ChartDefaults.BarChartStyleOptions.fontSize };

  return (
    <>
      <div
        style={{
          width: '100%',
          height: heightCss,
          background: 'white',
        }}
        ref={chartRef}
        className={className}
      >
        <ResponsiveContainer>
          <BarChart
            margin={margins}
            data={chartData}
            layout="vertical"
            style={{
              fontFamily: ChartDefaults.BarChartStyleOptions.fontFamily,
            }}
          >
            <XAxis type="number" domain={[0, xDomainMax]} style={axisTickStyle} />
            <YAxis
              type="category"
              dataKey="name"
              interval={0}
              style={axisTickStyle}
              width={yAxisWidth}
              tickFormatter={(value) => clipYAxisLabel(value, yAxisWidth)}
            />
            {baselineData && (
              <Bar
                isAnimationActive={false}
                dataKey="baseline"
                fill={generalConfig.colorBaseline || ChartDefaults.ColorBaseline}
                label={{
                  position: 'right',
                  formatter: labelFormatter,
                  style: {
                    fontSize: ChartDefaults.BarChartStyleOptions.fontSize,
                    fontWeight: ChartDefaults.BarChartStyleOptions.fontWeight,
                  },
                }}
              />
            )}
            {userData && (
              <Bar
                isAnimationActive={false}
                dataKey="user"
                fill={generalConfig.colorUser || ChartDefaults.ColorUser}
                label={{
                  position: 'right',
                  formatter: labelFormatter,
                  style: {
                    fontSize: ChartDefaults.BarChartStyleOptions.fontSize,
                    fontWeight: ChartDefaults.BarChartStyleOptions.fontWeight,
                  },
                }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};
